/**
 * Slide-out cart drawer for Warehouse.
 *
 * - Opens when the header cart icon is clicked
 * - Opens automatically when a product is added (theme fires "product:added")
 * - Quantity update / remove / order note through the AJAX Cart API
 * - Re-renders itself from /cart?view=drawer (templates/cart.drawer.liquid)
 * - Free shipping bar reads the shared country tier table (window.FSShip,
 *   defined by snippets/free-shipping-tiers.liquid) so it always agrees with
 *   the box under the Add to cart button on product pages.
 */
(function () {
  'use strict';

  var drawer = document.getElementById('cart-drawer');
  if (!drawer) {
    return;
  }

  var routes = window.routes || {};
  var cartUrl = routes.cartUrl || '/cart';
  var changeUrl = (routes.cartChangeUrl || cartUrl + '/change') + '.js';
  var updateUrl = cartUrl + '/update.js';

  var content = drawer.querySelector('[data-cart-drawer-content]');
  var openOnAdd = drawer.getAttribute('data-open-on-add') !== 'false';
  var lastFocused = null;
  var noteTimer = null;
  var refreshing = false;
  var selfTriggeredRefresh = false;
  var shippingCountry = '';

  drawer.removeAttribute('hidden');

  /* ---------------------------------------------------------------- */
  /* Open / close                                                      */
  /* ---------------------------------------------------------------- */

  function open() {
    if (drawer.classList.contains('is-open')) {
      return;
    }

    lastFocused = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('cart-drawer-open');
    document.body.classList.add('cart-drawer-open');

    var closeButton = drawer.querySelector('[data-cart-drawer-close]');
    if (closeButton) {
      window.setTimeout(function () {
        closeButton.focus();
      }, 50);
    }
  }

  function close() {
    if (!drawer.classList.contains('is-open')) {
      return;
    }

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('cart-drawer-open');
    document.body.classList.remove('cart-drawer-open');

    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
    lastFocused = null;
  }

  function setBusy(state) {
    if (state) {
      drawer.classList.add('is-busy');
    } else {
      drawer.classList.remove('is-busy');
    }
  }

  /* ---------------------------------------------------------------- */
  /* Free shipping progress                                            */
  /* ---------------------------------------------------------------- */

  function formatAmount(value) {
    return '$' + (Math.round(value * 100) / 100).toFixed(2);
  }

  function updateShippingBar() {
    var box = content.querySelector('[data-cart-drawer-shipping]');
    if (!box) {
      return;
    }

    var textEl = box.querySelector('[data-shipping-text]');
    var barEl = box.querySelector('[data-shipping-progress]');
    var track = box.querySelector('.cart-drawer__shipping-track');
    var total = (parseInt(box.getAttribute('data-cart-total'), 10) || 0) / 100;

    var threshold = 0;
    var countryName = '';

    if (window.FSShip) {
      // Shared tier table is available: only ever show a country-specific goal.
      var info = shippingCountry ? window.FSShip.info(shippingCountry) : null;
      if (!info) {
        box.hidden = true;
        return;
      }
      threshold = info.threshold;
      countryName = info.name;
    } else {
      // No shared table on the page: fall back to the flat theme setting.
      threshold = (parseInt(box.getAttribute('data-fallback-threshold'), 10) || 0) / 100;
    }

    if (!threshold || threshold <= 0) {
      box.hidden = true;
      return;
    }

    var remaining = threshold - total;
    var percent = Math.max(0, Math.min(100, (total / threshold) * 100));

    while (textEl.firstChild) {
      textEl.removeChild(textEl.firstChild);
    }

    if (remaining > 0) {
      textEl.className = 'cart-drawer__shipping-text';

      var amount = document.createElement('span');
      amount.className = 'cart-drawer__shipping-amount';
      amount.appendChild(document.createTextNode(formatAmount(remaining)));
      textEl.appendChild(amount);
      textEl.appendChild(
        document.createTextNode(
          ' away from free shipping' + (countryName ? ' to ' + countryName : '')
        )
      );
    } else {
      textEl.className = 'cart-drawer__shipping-text cart-drawer__shipping-text--success';
      textEl.appendChild(
        document.createTextNode(
          countryName
            ? 'Free shipping to ' + countryName + ' unlocked!'
            : 'You are eligible for free shipping!'
        )
      );
      percent = 100;
    }

    barEl.style.width = percent + '%';
    if (track) {
      track.setAttribute('aria-valuenow', Math.round(percent));
    }
    box.hidden = false;
  }

  function initShippingCountry() {
    if (!window.FSShip) {
      updateShippingBar();
      return;
    }

    var stored = window.FSShip.saved();
    if (stored && window.FSShip.info(stored)) {
      shippingCountry = stored;
      updateShippingBar();
    }

    window.FSShip.resolve().then(function (code) {
      if (code && code !== shippingCountry) {
        shippingCountry = code;
      }
      updateShippingBar();
    });
  }

  document.addEventListener('fsship:country', function (event) {
    var code = event.detail && event.detail.code;
    if (code) {
      shippingCountry = code;
      updateShippingBar();
    }
  });

  /* ---------------------------------------------------------------- */
  /* Rendering                                                         */
  /* ---------------------------------------------------------------- */

  function syncHeaderCount() {
    var inner = content.querySelector('[data-cart-item-count]');
    if (!inner) {
      return;
    }

    var count = inner.getAttribute('data-cart-item-count');
    var badges = document.querySelectorAll('.header__cart-count');

    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = count;
    }

    if (window.theme) {
      window.theme.cartCount = parseInt(count, 10) || 0;
    }
  }

  function refresh() {
    if (refreshing) {
      return Promise.resolve();
    }

    refreshing = true;
    setBusy(true);

    var url = cartUrl + (cartUrl.indexOf('?') === -1 ? '?' : '&') + 'view=drawer&t=' + new Date().getTime();

    return fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        content.innerHTML = html;
        syncHeaderCount();
        updateShippingBar();
      })
      .catch(function () {
        /* keep the previous markup on failure */
      })
      .then(function () {
        refreshing = false;
        setBusy(false);
      });
  }

  function notifyTheme() {
    selfTriggeredRefresh = true;
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', { bubbles: true, detail: { scrollToTop: false } })
    );
    window.setTimeout(function () {
      selfTriggeredRefresh = false;
    }, 400);
  }

  /* ---------------------------------------------------------------- */
  /* Cart mutations                                                    */
  /* ---------------------------------------------------------------- */

  function changeQuantity(lineKey, quantity) {
    setBusy(true);

    return fetch(changeUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ id: lineKey, quantity: quantity })
    })
      .then(function (response) {
        return response.json();
      })
      .then(function () {
        return refresh();
      })
      .then(function () {
        notifyTheme();
      })
      .catch(function () {
        setBusy(false);
      });
  }

  function saveNote(value) {
    fetch(updateUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ note: value })
    }).catch(function () {});
  }

  /* ---------------------------------------------------------------- */
  /* Events                                                            */
  /* ---------------------------------------------------------------- */

  // Header cart icon: take over the click before the theme's mini-cart does.
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') {
        return;
      }

      var toggle = target.closest('.header__cart-toggle, [data-action="toggle-mini-cart"]');
      if (!toggle) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (drawer.classList.contains('is-open')) {
        close();
      } else {
        open();
        refresh();
      }
    },
    true
  );

  // Inside the drawer
  drawer.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') {
      return;
    }

    if (target.closest('[data-cart-drawer-overlay]') || target.closest('[data-cart-drawer-close]')) {
      event.preventDefault();
      close();
      return;
    }

    var action = target.closest('[data-drawer-action="change-quantity"]');
    if (action) {
      event.preventDefault();
      changeQuantity(action.getAttribute('data-line-key'), parseInt(action.getAttribute('data-quantity'), 10));
    }
  });

  drawer.addEventListener('change', function (event) {
    var input = event.target;

    if (input.matches && input.matches('[data-drawer-action="set-quantity"]')) {
      var quantity = parseInt(input.value, 10);
      if (isNaN(quantity) || quantity < 0) {
        quantity = 0;
      }
      changeQuantity(input.getAttribute('data-line-key'), quantity);
    }
  });

  drawer.addEventListener('input', function (event) {
    if (!event.target.matches || !event.target.matches('[data-cart-drawer-note]')) {
      return;
    }

    var value = event.target.value;
    window.clearTimeout(noteTimer);
    noteTimer = window.setTimeout(function () {
      saveNote(value);
    }, 700);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' || event.keyCode === 27) {
      close();
    }
  });

  // Theme events
  document.addEventListener('product:added', function () {
    refresh().then(function () {
      if (openOnAdd) {
        open();
      }
    });
  });

  document.addEventListener('cart:refresh', function () {
    if (selfTriggeredRefresh) {
      return;
    }
    refresh();
  });

  initShippingCountry();
})();
